// src/services/serviceConsumptionService.js
//
// Frontend API wrapper for Service Material Consumption / salon BOM.
// =============================================================================
//
// PURPOSE
// -------
// Keeps service material rule and material review API calls in one place. UI
// components stay focused on forms/tables while the backend remains
// authoritative for validation, unit conversion, stock availability checks and
// inventory posting.
//
// BUSINESS LANGUAGE
// -----------------
// Service material rules are the salon-friendly equivalent of a lightweight BOM:
// "Massage 60 min consumes 20 ml of oil and 1 towel." The rules do not change
// stock. Stock changes are generated later when a visit is checked or when a
// manager resolves/corrects a material review row.

import apiClient from "../utils/apiClient";
import { idempotentPost } from "../utils/idempotentStockPosting";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status || err?.status;
  e.outcomeUnknown = Boolean(err?.outcomeUnknown || err?.response?.data?.outcome_unknown);
  e.data = err?.response?.data || err?.data;
  e.code = err?.response?.data?.code || err?.code;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[ServiceConsumptionService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

function assertId(context, id) {
  if (id === undefined || id === null || `${id}`.trim() === "") {
    const e = new Error(`${context}: id is required.`);
    e.code = "ID_MISSING";
    throw e;
  }
}

function cleanOptionalText(v) {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s || null;
}

function cleanNumberLike(v) {
  if (v === undefined || v === null || `${v}`.trim() === "") return null;
  return String(v).replace(",", ".");
}

function normalizeResolutionPayload(payload = {}) {
  return {
    business_date: cleanOptionalText(payload.business_date),
    product_id: payload.product_id || null,
    location_id: payload.location_id || null,
    comment: cleanOptionalText(payload.comment),
  };
}

export const CONSUMPTION_MODES = Object.freeze({
  FIXED: "fixed",
  PER_DURATION: "per_duration",
  MANUAL: "manual",
});

export const listServiceConsumptionRules = async (serviceId, params = {}) => {
  try {
    assertId("listServiceConsumptionRules", serviceId);

    const res = await apiClient.get(`/service-consumption/services/${serviceId}/rules`, {
      withCredentials: true,
      params,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("listServiceConsumptionRules", err);
  }
};

export const createServiceConsumptionRule = async (serviceId, payload = {}) => {
  try {
    assertId("createServiceConsumptionRule", serviceId);

    const res = await apiClient.post(
      `/service-consumption/services/${serviceId}/rules`,
      normalizeRulePayload(payload),
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    return res?.data;
  } catch (err) {
    throwApiError("createServiceConsumptionRule", err);
  }
};

export const updateServiceConsumptionRule = async (ruleId, payload = {}) => {
  try {
    assertId("updateServiceConsumptionRule", ruleId);

    const res = await apiClient.put(
      `/service-consumption/rules/${ruleId}`,
      normalizeRulePayload(payload),
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    return res?.data;
  } catch (err) {
    throwApiError("updateServiceConsumptionRule", err);
  }
};

export const deleteServiceConsumptionRule = async (ruleId) => {
  try {
    assertId("deleteServiceConsumptionRule", ruleId);

    const res = await apiClient.delete(`/service-consumption/rules/${ruleId}`, {
      withCredentials: true,
    });

    return res?.data || { ok: true };
  } catch (err) {
    throwApiError("deleteServiceConsumptionRule", err);
  }
};

export const listMaterialConsumptionExceptions = async (params = {}) => {
  try {
    const res = await apiClient.get("/service-consumption/exceptions", {
      withCredentials: true,
      params,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("listMaterialConsumptionExceptions", err);
  }
};

export const listMaterialConsumptionReview = async (params = {}) => {
  try {
    const res = await apiClient.get("/service-consumption/review", {
      withCredentials: true,
      params,
    });

    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("listMaterialConsumptionReview", err);
  }
};

export const reprocessMaterialConsumption = async (id, payload = {}) => {
  try {
    assertId("reprocessMaterialConsumption", id);
    const res = await apiClient.post(`/service-consumption/material-consumptions/${id}/reprocess`, normalizeResolutionPayload(payload), {
      withCredentials: true,
    });
    return res?.data;
  } catch (err) {
    throwApiError("reprocessMaterialConsumption", err);
  }
};

export const skipMaterialConsumption = async (id, payload = {}) => {
  try {
    assertId("skipMaterialConsumption", id);
    const res = await apiClient.post(
      `/service-consumption/material-consumptions/${id}/skip`,
      { comment: cleanOptionalText(payload.comment) },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError("skipMaterialConsumption", err);
  }
};

export const useAnotherLocationForMaterial = async (id, payload = {}) => {
  try {
    assertId("useAnotherLocationForMaterial", id);
    const res = await apiClient.post(
      `/service-consumption/material-consumptions/${id}/use-location`,
      normalizeResolutionPayload(payload),
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError("useAnotherLocationForMaterial", err);
  }
};

export const useSubstituteProductForMaterial = async (id, payload = {}) => {
  try {
    assertId("useSubstituteProductForMaterial", id);
    const res = await apiClient.post(
      `/service-consumption/material-consumptions/${id}/use-substitute-product`,
      normalizeResolutionPayload(payload),
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError("useSubstituteProductForMaterial", err);
  }
};

export const replacePostedMaterialProduct = async (id, payload = {}) => {
  try {
    assertId("replacePostedMaterialProduct", id);
    const res = await idempotentPost(apiClient,
      `/service-consumption/material-consumptions/${id}/replace-product`,
      normalizeResolutionPayload(payload),
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError("replacePostedMaterialProduct", err);
  }
};

function normalizeRulePayload(payload = {}) {
  return {
    product_id: payload.product_id,
    consumption_mode: payload.consumption_mode || CONSUMPTION_MODES.FIXED,
    quantity: cleanNumberLike(payload.quantity),
    unit_code: cleanOptionalText(payload.unit_code),
    duration_unit_minutes: cleanNumberLike(payload.duration_unit_minutes),
    default_location_id: payload.default_location_id || null,
    location_policy: payload.location_policy || "fixed_location",
    is_required: !!payload.is_required,
    is_active: payload.is_active !== false,
    allow_manual_change: payload.allow_manual_change !== false,
    auto_add_to_visit: payload.auto_add_to_visit !== false,
    shortage_policy: payload.shortage_policy || "create_exception",
    comment: cleanOptionalText(payload.comment),
  };
}
